import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import TextField from '@mui/material/TextField'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import { Link } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import palette from '../../../theme/palette'
import { awbLinkSx } from '../../common/sharedStyles'
import { post } from '../../common/constants'
import { useGetStuckShipmentsQuery } from '../../redux/stuckShipmentsApiSlice'
import { BUCKETS, count, filenameFromResponse, groupLabel, saveBlob } from './stuckUtils'

const COLS = [
  { key: 'awbNumber', label: 'AWB' },
  { key: 'orderReferenceNumber', label: 'Reference' },
  { key: 'merchantName', label: 'Merchant' },
  { key: 'courierName', label: 'Courier' },
  { key: 'prozoId', label: 'Prozo Id' },
  { key: 'orderStatus', label: 'Status' },
  { key: 'statusGroup', label: 'Group' },
  { key: 'stuckDays', label: 'Stuck days', num: true },
  { key: 'lastStatusUpdate', label: 'Last update' },
  { key: 'awbRegisteredDate', label: 'AWB date' },
  { key: 'expectedDate', label: 'Expected' },
  { key: 'tat', label: 'TAT' },
  { key: 'invoiceValue', label: 'Invoice ₹', num: true },
  { key: 'appointmentBased', label: 'Appointment' },
  { key: 'lastRemark', label: 'Last remark' },
  { key: 'pickupPincode', label: 'Pickup pin' },
  { key: 'dropPincode', label: 'Drop pin' },
]

/**
 * The mail's STUCK_SHIPMENT_* CSV, on screen: every stuck shipment in scope, oldest-stuck first, from the snapshot.
 * `scope` is the page's filter body; `bucket` (optional) narrows to one age bucket; `total` comes from the server (MySQL count).
 */
export default function StuckShipmentsTab({ scope, bucket, onClearBucket }) {
  const { enqueueSnackbar } = useSnackbar()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [exporting, setExporting] = useState(false)

  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 400); return () => clearTimeout(t) }, [search])

  const body = { ...scope, buckets: bucket ? [bucket] : [], search: debounced || undefined, page: page + 1, pageSize }
  const scopeKey = JSON.stringify({ ...body, page: undefined, pageSize: undefined })
  useEffect(() => { setPage(0) }, [scopeKey])

  const { data, isFetching, error } = useGetStuckShipmentsQuery(body)
  const rows = data?.rows || []
  const total = data?.totalRecords || 0
  const isPtl = scope.segment === 'PTL'
  const cols = COLS.filter(c => isPtl || c.key !== 'appointmentBased')

  const exportCsv = async () => {
    setExporting(true)
    try {
      const res = await post('ops/stuck-shipments/shipments/export', { ...body, page: undefined, pageSize: undefined }, { responseType: 'blob' })
      saveBlob(res.data, filenameFromResponse(res, `STUCK_SHIPMENT_${scope.segment}.csv`))
    } catch (e) {
      const status = e?.response?.status
      enqueueSnackbar(status === 409 ? 'Two exports are already running on the server. Try again in a minute.'
        : status === 404 ? 'No snapshot yet — refresh first.' : 'Export failed. Try again.', { variant: 'error' })
    } finally {
      setExporting(false)
    }
  }

  const cell = (r, c) => {
    const v = r[c.key]
    switch (c.key) {
      case 'awbNumber': return v ? <Box component={Link} to={`/order-detail/${encodeURIComponent(v)}`} sx={awbLinkSx}>{v}</Box> : '—'
      case 'statusGroup': return groupLabel(v)
      case 'stuckDays': return <strong>{v}</strong>
      case 'tat': return v === 'Breached'
        ? <Chip size='small' label='Breached' sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: palette.error.lighter, color: palette.error.dark }} />
        : (v || 'NA')
      case 'appointmentBased': return v ? 'Yes' : 'No'
      case 'invoiceValue': return v === null || v === undefined ? '—' : count(v)
      case 'lastRemark': return v ? <span title={v}>{v.length > 40 ? v.slice(0, 40) + '…' : v}</span> : '—'
      default: return v === null || v === undefined || v === '' ? '—' : String(v)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Oldest-stuck first, as captured in the snapshot. Age is time since the courier's last status update.
          </Typography>
          {bucket && <Chip size='small' label={BUCKETS.find(b => b.key === bucket)?.label || bucket} onDelete={onClearBucket} />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <TextField size='small' placeholder='AWB or reference' value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: 200 }} />
          <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />} onClick={exportCsv} disabled={exporting || Boolean(error) || !total} sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {exporting ? 'Preparing…' : `CSV · ${count(total)} rows`}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity='error' sx={{ mb: 1 }}>{error?.data?.message || error?.message || 'Could not load shipments.'}</Alert>}

      {isFetching ? (
        <Box sx={{ p: 2 }}>{[...Array(8)].map((_, i) => <Skeleton key={i} height={32} sx={{ mb: 0.5 }} />)}</Box>
      ) : !error && (
        <>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ '& td, & th': { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } }}>
              <TableHead>
                <TableRow>
                  {cols.map(c => <TableCell key={c.key} align={c.num ? 'right' : 'left'} sx={{ bgcolor: palette.grey[200], fontWeight: 700, color: 'text.secondary' }}>{c.label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.awbNumber || i} hover>
                    {cols.map(c => <TableCell key={c.key} align={c.num ? 'right' : 'left'}>{cell(r, c)}</TableCell>)}
                  </TableRow>
                ))}
                {!rows.length && <TableRow><TableCell colSpan={cols.length} align='center' sx={{ py: 4, color: 'text.disabled' }}>Nothing stuck in this scope</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component='div' count={total} page={page} rowsPerPage={pageSize}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0) }}
            rowsPerPageOptions={[50, 100, 200]}
          />
        </>
      )}
    </Box>
  )
}
